import {
  GetString,
} from 'fluent-react';
import React, { RefObject } from 'react';
import {
  CountryLookback,
  CountryProfile,
  GlobalDatum,
  LookBackYearRange,
  ProductClass,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import {
  IndicatorsContainerBase,
} from '../Grid';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Indicator, {
  Status,
  Type,
} from '../topPageIndicator/Indicator';
import { adjustRankValueDirection, numberToSign } from '../Utils';
import { defaultECIProductClass } from '../Utils';

const query = gql`
  query ExportComplexityTopOfPageIndicators(
    $id: ID!, $yearRange: LookBackYearRange!,  $productClass: ProductClass!
  ) {
    countryProfile(location: $id) {
      latestEciRank
    }
    countryLookback(id: $id, yearRange: $yearRange, productClass: $productClass) {
      eciRankChange
    }
    globalDatum {
      latestEciRankTotal
    }
  }
`;

interface SuccessResponse {
  countryProfile: null | {
    latestEciRank: CountryProfile['latestEciRank'],
  };
  countryLookback: null | {
    eciRankChange: CountryLookback['eciRankChange'],
  };
  globalDatum: {
    latestEciRankTotal: GlobalDatum['latestEciRankTotal'],
  };
}

interface Variables {
  id: string;
  yearRange: LookBackYearRange;
  productClass: ProductClass;
}

interface Props extends SlideElementProps {
  countryId: string;
  getFluentString: GetString;
  zIndex: number;
}

const TopOfPageIndicators = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const variables: Variables = {
    id: props.countryId,
    yearRange: LookBackYearRange.TenYears,
    productClass: defaultECIProductClass,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  const {
    getFluentString, zIndex,
  } = props;

  const eciRankLabel = (
    <GlossaryTerm
        type={TooltipType.Standard}
        term={getFluentString('eci-rank-indicator-label')}
        explanation={getFluentString('glossary-eci-term-definition')}
        citation={undefined}
        align={TooltipAlign.Standard}
        attachment={null}
      />
  );
  const eciRankChangelabel = getFluentString('eci-rank-change-indicator-label');

  const failedECIRank = (
    <Indicator
      type={Type.Rank}
      status={Status.Failure}
      label={eciRankLabel}
    />
  );
  const failedECIRankChange = (
    <Indicator
      type={Type.TemporalChange}
      status={Status.Failure}
      label={eciRankChangelabel}
      unit={getFluentString('eci-rank-change-indicator-unit', {quantity: 2})}
    />
  );

  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = (
      <>
        <Indicator
          type={Type.Rank}
          status={Status.Loading}
          label={eciRankLabel}
        />
        <Indicator
          type={Type.TemporalChange}
          status={Status.Loading}
          label={eciRankChangelabel}
          unit={getFluentString('eci-rank-change-indicator-unit', {quantity: 2})}
        />
      </>
    );
  } else if (error !== undefined) {
    console.error(error);
    out = (
      <>
        {failedECIRank} {failedECIRankChange}
      </>
    );
  } else if (data !== undefined) {
    const {countryProfile, countryLookback, globalDatum} = data;
    if (countryProfile === null || countryLookback === null || globalDatum === null) {
      out = (
        <>{failedECIRank} {failedECIRankChange}</>
      );
    } else {
      const latestEciRankTotal = globalDatum.latestEciRankTotal;
      const eciRankChange = countryLookback.eciRankChange;
      const latestEciRank = countryProfile.latestEciRank;
      // Adjust ECI rank change so that the smallest number (position 1)
      // is considered the best instead of the largest number (last place)
      const eciRankChangeAdjustedDirection = adjustRankValueDirection(eciRankChange);
      const eciRankIndicator = (latestEciRank === null) ? failedECIRank : (
        <Indicator
          type={Type.Rank}
          status={Status.Success}
          label={eciRankLabel}
          numerator={latestEciRank}
          suffix={getFluentString('ordinal-rank-without-number', {rank: latestEciRank})}
          denominator={getFluentString('eci-rank-indicator-denominator', {total: latestEciRankTotal})}
        />
      );
      const eciRankChangeIndicator = (
        <Indicator
          type={Type.TemporalChange}
          status={Status.Success}
          label={eciRankChangelabel}
          quantity={Math.abs(eciRankChange)}
          changeDirection={numberToSign(eciRankChangeAdjustedDirection)}
          unit={getFluentString('eci-rank-change-indicator-unit', {quantity: eciRankChange})}
        />
      );
      out = (
        <>{eciRankIndicator} {eciRankChangeIndicator}</>
      );
    }
  } else {
    out = null;
  }
  const style: React.CSSProperties = {
    zIndex,
  };
  return (
    <IndicatorsContainerBase
      style={style}
      ref={rootElRef}
    >
      {out}
    </IndicatorsContainerBase>
  );
});

export default TopOfPageIndicators;
